import React from "react";
import { HistoryIcon } from "../../assets/Icons/HistoryIcon";
import { DatabaseIcon } from "../../assets/Icons/Database";
import { PeopleIcon } from "../../assets/Icons/People";
import { WarningCircle } from "../../assets/Icons/WarningCircle";
import { Presentation } from "../../assets/Icons/Presentation";
import { UploadSquare } from "../../assets/Icons/UploadSquare";
import { MagnifyingGlass } from "../../assets/Icons/MagnifyingGlass";
import BookIcon from "../../assets/Icons/BookIcon";
import DatabaseSearchIcon from "../../assets/Icons/DatabaseSearchIcon";
import ColorCalendar from "../../assets/Icons/ColorCalendar";
import { CalendarMinusIcon } from "../../assets/Icons/CalendarMinusIcon";
import { ActionNeededIcon } from "../../assets/Icons/ActionNeededIcon";

// Mapping from smart suggestion text to static_view_type
// These suggestions will skip AI API call and directly fetch orders
// Note: "my weekly orders" is role-dependent and handled separately
const SMART_SUGGESTION_TO_STATIC_VIEW = {
  // Plan my day variations
  "please plan my day": "plan_my_day",
  "plan my day": "plan_my_day",
  // Data triage
  "orders in data triage": "orders_data_triage",
  // Newly allocated
  "newly allocated orders": "newly_allocated_orders",
  // Aging orders
  "aging orders": "aging_orders",
  "ageing orders": "aging_orders",
  "ageing order": "aging_orders",
  // Pending review
  "pending at l1/l2 review": "pending_review",
  // High iteration
  "high iteration orders": "high_iteration_orders",
  // Awaiting response
  "awaiting response from at": "awaiting_response_orders",
  // Orders near due date
  "orders nearing due date": "orders_near_duedate",
  // Allocation review
  "allocation review requested": "allocation_review_request",
  // Action needed
  "action needed": "action_needed_orders",
};

// Helper function to get static_view_type from suggestion text
// selectedRole is used for role-dependent mappings like "my weekly orders"
export const getStaticViewTypeFromText = (text, selectedRole) => {
  const normalized = String(text || "")
    .toLowerCase()
    .trim();

  // Handle "my weekly orders" based on role
  if (normalized === "my weekly orders") {
    const roleName =
      typeof selectedRole === "object"
        ? (selectedRole?.roleName || selectedRole?.name || "").toUpperCase()
        : String(selectedRole || "").toUpperCase();

    if (roleName === "REGIONAL LEAD") {
      return "regional_leads_weekly_orders";
    }
    return "my_weekly_orders";
  }

  return SMART_SUGGESTION_TO_STATIC_VIEW[normalized] || null;
};

// Role-based inline suggestions (displayed below chatbox)
// Each role has 3 quick action buttons + "See All"
export const getRoleBasedInlineSuggestions = ({
  onGenericAction,
  onPlanMyDay,
  onOrdersInDataTriage,
  onNewlyAllocatedOrders,
  selectedRole,
}) => {
  const roleName =
    typeof selectedRole === "object"
      ? (selectedRole?.roleName || selectedRole?.name || "").toUpperCase()
      : String(selectedRole || "").toUpperCase();

  // Define role-based inline suggestions (3 buttons per role)
  const roleInlineSuggestions = {
    DID: [
      {
        icon: <HistoryIcon width={20} height={20} />,
        text: "Please plan my day",
        action: () => onGenericAction?.("Please plan my day"),
      },
      {
        icon: <DatabaseIcon width={20} height={20} />,
        text: "Orders in Data Triage",
        action: () => onGenericAction?.("Orders in Data Triage"),
      },
      {
        icon: <ColorCalendar color="#FF7D1E" />,
        text: "My weekly orders",
        action: () => onGenericAction?.("My weekly orders"),
      },
    ],

    "REGIONAL LEAD": [
      {
        icon: <HistoryIcon width={20} height={20} />,
        text: "Please plan my day",
        action: () => onGenericAction?.("Please plan my day"),
      },
      {
        icon: <DatabaseIcon width={20} height={20} />,
        text: "Order by Status",
        action: () => onGenericAction?.("Order by Status"),
      },
      {
        icon: <MagnifyingGlass width={20} height={20} color="#B400FF" />,
        text: "Allocation Review requested",
        action: () => onGenericAction?.("Allocation Review requested"),
      },
    ],
    // Default fallback for unknown roles
    DEFAULT: [
      {
        icon: <HistoryIcon width={20} height={20} />,
        text: "Please plan my day",
        action: onPlanMyDay,
      },
      {
        icon: <DatabaseSearchIcon color="#4696FF" />,
        text: "Orders in Data Triage",
        action: onOrdersInDataTriage,
      },
      {
        icon: <PeopleIcon width={20} height={20} />,
        text: "Newly allocated orders",
        action: onNewlyAllocatedOrders,
      },
    ],
  };

  return roleInlineSuggestions[roleName] || roleInlineSuggestions.DEFAULT;
};

// Role-based popup suggestions (displayed in "See All" modal)
// Each role has different suggestions per tab
export const getRoleBasedPopupSuggestions = ({
  onGenericAction,
  onPlanMyDay,
  onOrdersInDataTriage,
  onNewlyAllocatedOrders,
  selectedRole,
}) => {
  const roleName =
    typeof selectedRole === "object"
      ? (selectedRole?.roleName || selectedRole?.name || "").toUpperCase()
      : String(selectedRole || "").toUpperCase();

  // Define role-based popup suggestions
  const rolePopupSuggestions = {
    DID: {
      "AI Suggestions": [
        {
          icon: <WarningCircle width={20} height={20} color="#4696FF" />,
          text: "Ageing Orders",
          action: () => onGenericAction?.("Aging Orders"),
        },
        {
          icon: <MagnifyingGlass width={20} height={20} color="#00C864" />,
          text: "In Review",
          action: () => onGenericAction?.("Pending at L1/L2 review"),
        },
        {
          icon: <UploadSquare width={20} height={20} color="#B4FF00" />,
          text: "Awaiting response from AT",
          action: () => onGenericAction?.("Awaiting response from AT"),
        },
        {
          icon: <PeopleIcon width={20} height={20} color="#FFE600" />,
          text: "Newly Allocated Orders",
          action: () => onGenericAction?.("Newly Allocated Orders"),
        },
        {
          icon: <DatabaseIcon width={20} height={20} />,
          text: "Order by Status",
          action: () => onGenericAction?.("Order by Status"),
        },
        // {
        //   icon: <BookIcon color="#FF7D1E" />,
        //   text: "What are mandatory fields in JE?",
        //   action: () => onGenericAction?.("What are mandatory fields in JE?"),
        // },
        // {
        //   icon: <BookIcon color="#B4FF00" />,
        //   text: "What is Retained Earnings?",
        //   action: () => onGenericAction?.("What is Retained Earnings?"),
        // },
      ],
      "Most used": [
        {
          icon: <HistoryIcon width={20} height={20} color="#FF7D1E" />,
          text: "Please plan my day",
          action: onPlanMyDay,
        },
        {
          icon: <DatabaseSearchIcon color="#4696FF" />,
          text: "Orders in Data Triage",
          action: onOrdersInDataTriage,
        },
        {
          icon: <PeopleIcon width={20} height={20} />,
          text: "Newly allocated orders",
          action: onNewlyAllocatedOrders,
        },
        {
          icon: <UploadSquare width={20} height={20} color="#B4FF00" />,
          text: "Awaiting response from AT",
          action: () => onGenericAction?.("Awaiting response from AT"),
        },
        {
          icon: <MagnifyingGlass width={20} height={20} color="#00C864" />,
          text: "In Review",
          action: () => onGenericAction?.("Pending at L1/L2 review"),
        },
        {
          icon: <WarningCircle width={20} height={20} color="#4696FF" />,
          text: "Ageing orders",
          action: () => onGenericAction?.("Aging orders"),
        },
      ],
      "Knowledge Prompts": [
        {
          icon: <BookIcon color="#FF7D1E" />,
          text: "What are mandatory fields in JE?",
          action: () => onGenericAction?.("What are mandatory fields in JE?"),
        },
        {
          icon: <BookIcon color="#4696FF" />,
          text: "What is Journal Entry?",
          action: () => onGenericAction?.("What is Journal Entry?"),
        },
        {
          icon: <BookIcon color="#B400FF" />,
          text: "What is Trial Balance?",
          action: () => onGenericAction?.("What is Trial Balance?"),
        },
        {
          icon: <BookIcon color="#B4FF00" />,
          text: "What is Retained Earnings?",
          action: () => onGenericAction?.("What is Retained Earnings?"),
        },
        {
          icon: <BookIcon color="#00C864" />,
          text: "What is Chart of Accounts?",
          action: () => onGenericAction?.("What is Chart of Accounts?"),
        },
        {
          icon: <BookIcon color="#32FFFF" />,
          text: "Tips & tricks to resolve variances",
          action: () => onGenericAction?.("Tips & tricks to resolve variances"),
        },
        {
          icon: <BookIcon color="#FFE600" />,
          text: "When is compression done?",
          action: () => onGenericAction?.("When is compression done?"),
        },
      ],
    },
    "REGIONAL LEAD": {
      "AI Suggestions": [
        {
          icon: <WarningCircle width={20} height={20} color="#4696FF" />,
          text: "Ageing Order",
          action: () => onGenericAction?.("Ageing Order"),
        },
        {
          icon: <HistoryIcon width={20} height={20} color="#00C864" />,
          text: "Order creation pending",
          action: () => onGenericAction?.("Order creation pending"),
        },
        {
          icon: <CalendarMinusIcon color="#EA011D" />,
          text: "Orders Nearing Due Date",
          action: () => onGenericAction?.("Orders Nearing Due Date"),
        },
        // {
        //   icon: <UploadSquare width={20} height={20} color="#B4FF00" />,
        //   text: "Awaiting response from AT",
        //   action: () => onGenericAction?.("Awaiting response from AT"),
        // },
        {
          icon: <ColorCalendar color="#FF7D1E" />,
          text: "My weekly orders",
          action: () => onGenericAction?.("My weekly orders"),
        },
        {
          icon: <ActionNeededIcon width={20} height={20} color="#FF6B6B" />,
          text: "Action Needed",
          action: () => onGenericAction?.("Action Needed"),
        },
        // {
        //   icon: <BookIcon color="#B4FF00" />,
        //   text: "What is Retained Earnings?",
        //   action: () => onGenericAction?.("What is Retained Earnings?"),
        // },
      ],
      "Most used": [
        {
          icon: <HistoryIcon width={20} height={20} color="#FF7D1E" />,
          text: "Please plan my day",
          action: () => onGenericAction?.("Please plan my day"),
        },
        {
          icon: <DatabaseIcon width={20} height={20} />,
          text: "Order by Status",
          action: () => onGenericAction?.("Order by Status"),
        },
        {
          icon: <MagnifyingGlass width={20} height={20} color="#B400FF" />,
          text: "Allocation Review requested",
          action: () => onGenericAction?.("Allocation Review requested"),
        },
        {
          icon: <ColorCalendar color="#FF7D1E" />,
          text: "My weekly orders",
          action: () => onGenericAction?.("My weekly orders"),
        },
        {
          icon: <WarningCircle width={20} height={20} color="#4696FF" />,
          text: "Ageing Order",
          action: () => onGenericAction?.("Ageing Order"),
        },
        {
          icon: <Presentation width={20} height={20} color="#FF6B6B" />,
          text: "Action Needed",
          action: () => onGenericAction?.("Action Needed"),
        },
      ],

      "Knowledge Prompts": [
        {
          icon: <BookIcon color="#FF7D1E" />,
          text: "What are mandatory fields in JE?",
          action: () => onGenericAction?.("What are mandatory fields in JE?"),
        },
        {
          icon: <BookIcon color="#4696FF" />,
          text: "What is Journal Entry?",
          action: () => onGenericAction?.("What is Journal Entry?"),
        },
        {
          icon: <BookIcon color="#B400FF" />,
          text: "What is Trial Balance?",
          action: () => onGenericAction?.("What is Trial Balance?"),
        },
        {
          icon: <BookIcon color="#B4FF00" />,
          text: "What is Retained Earnings?",
          action: () => onGenericAction?.("What is Retained Earnings?"),
        },
        {
          icon: <BookIcon color="#00C864" />,
          text: "What is Chart of Accounts?",
          action: () => onGenericAction?.("What is Chart of Accounts?"),
        },
        {
          icon: <BookIcon color="#32FFFF" />,
          text: "Tips & tricks to resolve variances",
          action: () => onGenericAction?.("Tips & tricks to resolve variances"),
        },
        {
          icon: <BookIcon color="#FFE600" />,
          text: "When is compression done?",
          action: () => onGenericAction?.("When is compression done?"),
        },
      ],
    },
  };

  // Default suggestions for unknown roles (original behavior)
  const defaultSuggestions = {
    "AI Suggestions": [
      {
        icon: <WarningCircle width={20} height={20} color="#4696FF" />,
        text: "Ageing orders",
        action: () => onGenericAction?.("Aging orders"),
      },
      {
        icon: <MagnifyingGlass width={20} height={20} color="#00C864" />,
        text: "In Review",
        action: () => onGenericAction?.("Pending at L1/L2 review"),
      },
      {
        icon: <UploadSquare width={20} height={20} color="#B4FF00" />,
        text: "Awaiting response from AT",
        action: () => onGenericAction?.("Awaiting response from AT"),
      },
      {
        icon: <ColorCalendar color="#FF7D1E" />,
        text: "My weekly orders",
        action: () => onGenericAction?.("My weekly orders"),
      },
      {
        icon: <BookIcon color="#FF7D1E" />,
        text: "What are mandatory fields in JE ?",
        action: () => onGenericAction?.("What are mandatory fields in JE ?"),
      },
      {
        icon: <BookIcon color="#B4FF00" />,
        text: "What is Retained Earnings?",
        action: () => onGenericAction?.("What is Retained Earnings?"),
      },
    ],
    "Most used": [
      {
        icon: <HistoryIcon width={20} height={20} color="#FF7D1E" />,
        text: "Please plan my day",
        action: onPlanMyDay,
      },
      {
        icon: <DatabaseSearchIcon color="#4696FF" />,
        text: "Orders in Data Triage",
        action: onOrdersInDataTriage,
      },
      {
        icon: <PeopleIcon width={20} height={20} />,
        text: "Newly allocated orders",
        action: onNewlyAllocatedOrders,
      },
      {
        icon: <UploadSquare width={20} height={20} color="#B4FF00" />,
        text: "Awaiting response from AT",
        action: () => onGenericAction?.("Awaiting response from AT"),
      },
      {
        icon: <MagnifyingGlass width={20} height={20} color="#00C864" />,
        text: "In Review",
        action: () => onGenericAction?.("Pending at L1/L2 review"),
      },
      {
        icon: <WarningCircle width={20} height={20} color="#4696FF" />,
        text: "Ageing orders",
        action: () => onGenericAction?.("Aging orders"),
      },
      {
        icon: <Presentation width={20} height={20} color="#B400FF" />,
        text: "High iteration orders",
        action: () => onGenericAction?.("High iteration orders"),
      },
    ],
    "Knowledge Prompts": [
      {
        icon: <BookIcon color="#FF7D1E" />,
        text: "What are mandatory fields in JE ?",
        action: () => onGenericAction?.("What are mandatory fields in JE ?"),
      },
      {
        icon: <BookIcon color="#4696FF" />,
        text: "What is Journal Entry / General Ledger?",
        action: () =>
          onGenericAction?.("What is Journal Entry / General Ledger?"),
      },
      {
        icon: <BookIcon color="#B400FF" />,
        text: "What is Trial Balance?",
        action: () => onGenericAction?.("What is Trial Balance?"),
      },
      {
        icon: <BookIcon color="#B4FF00" />,
        text: "What is Retained Earnings?",
        action: () => onGenericAction?.("What is Retained Earnings?"),
      },
      {
        icon: <BookIcon color="#00C864" />,
        text: "What is Chart of Accounts or CoA?",
        action: () => onGenericAction?.("What is Chart of Accounts or CoA?"),
      },
      {
        icon: <BookIcon color="#32FFFF" />,
        text: "Tips & tricks to resolve variances",
        action: () => onGenericAction?.("Tips & tricks to resolve variances"),
      },
      {
        icon: <BookIcon color="#FFE600" />,
        text: "When is compression done?",
        action: () => onGenericAction?.("When is compression done?"),
      },
    ],
  };

  return rolePopupSuggestions[roleName] || defaultSuggestions;
};

// Legacy function for backward compatibility
export const getSuggestionsByTab = ({
  onPlanMyDay,
  onOrdersInDataTriage,
  onNewlyAllocatedOrders,
  onGenericAction,
}) => ({
  "AI Suggestions": [
    {
      icon: <WarningCircle width={20} height={20} color="#4696FF" />,
      text: "Ageing orders",
      action: () => onGenericAction?.("Aging orders"),
    },
    {
      icon: <MagnifyingGlass width={20} height={20} color="#00C864" />,
      text: "In Review",
      action: () => onGenericAction?.("Pending at L1/L2 review"),
    },
    {
      icon: <UploadSquare width={20} height={20} color="#B4FF00" />,
      text: "Awaiting response from AT",
      action: () => onGenericAction?.("Awaiting response from AT"),
    },
    {
      icon: <ColorCalendar color="#FF7D1E" />,
      text: "My weekly orders",
      action: () => onGenericAction?.("My weekly orders"),
    },
    {
      icon: <BookIcon color="#FF7D1E" />,
      text: "What are mandatory fields in JE ?",
      action: () => onGenericAction?.("What are mandatory fields in JE ?"),
    },
    {
      icon: <BookIcon color="#B4FF00" />,
      text: "What is Retained Earnings?",
      action: () => onGenericAction?.("What is Retained Earnings?"),
    },
  ],
  "Most used": [
    {
      icon: <HistoryIcon width={20} height={20} color="#FF7D1E" />,
      text: "Please plan my day",
      action: onPlanMyDay,
    },
    {
      icon: <DatabaseSearchIcon color="#4696FF" />,
      text: "Orders in Data Triage",
      action: onOrdersInDataTriage,
    },
    {
      icon: <PeopleIcon width={20} height={20} />,
      text: "Newly allocated orders",
      action: onNewlyAllocatedOrders,
    },
    {
      icon: <UploadSquare width={20} height={20} color="#B4FF00" />,
      text: "Awaiting response from AT",
      action: () => onGenericAction?.("Awaiting response from AT"),
    },
    {
      icon: <MagnifyingGlass width={20} height={20} color="#00C864" />,
      text: "In Review",
      action: () => onGenericAction?.("Pending at L1/L2 review"),
    },
    {
      icon: <WarningCircle width={20} height={20} color="#4696FF" />,
      text: "Ageing orders",
      action: () => onGenericAction?.("Aging orders"),
    },
    {
      icon: <Presentation width={20} height={20} color="#B400FF" />,
      text: "High iteration orders",
      action: () => onGenericAction?.("High iteration orders"),
    },
  ],
  "Knowledge Prompts": [
    {
      icon: <BookIcon color="#FF7D1E" />,
      text: "What are mandatory fields in JE ?",
      action: () => onGenericAction?.("What are mandatory fields in JE ?"),
    },
    {
      icon: <BookIcon color="#4696FF" />,
      text: "What is Journal Entry / General Ledger?",
      action: () =>
        onGenericAction?.("What is Journal Entry / General Ledger?"),
    },
    {
      icon: <BookIcon color="#B400FF" />,
      text: "What is Trial Balance?",
      action: () => onGenericAction?.("What is Trial Balance?"),
    },
    {
      icon: <BookIcon color="#B4FF00" />,
      text: "What is Retained Earnings?",
      action: () => onGenericAction?.("What is Retained Earnings?"),
    },
    {
      icon: <BookIcon color="#00C864" />,
      text: "What is Chart of Accounts or CoA?",
      action: () => onGenericAction?.("What is Chart of Accounts or CoA?"),
    },
    {
      icon: <BookIcon color="#32FFFF" />,
      text: "Tips & tricks to resolve variances",
      action: () => onGenericAction?.("Tips & tricks to resolve variances"),
    },
    {
      icon: <BookIcon color="#FFE600" />,
      text: "When is compression done?",
      action: () => onGenericAction?.("When is compression done?"),
    },
  ],
});
